import React, {useEffect, useRef, useState, useCallback} from "react";
import {headerWrapCss, logoBoxCss, menuCss, smallIconCss, authButtonsCss, bookPairCss} from "./Header.style";
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';

import {ReactComponent as Menu} from 'src/assets/menu.svg';
import {ReactComponent as MobileMenu} from 'src/assets/mobileMenu.svg';
import DropdownMenu from "./DropdownMenu";
import NotificationDropdown from "./NotificationDropdown";
import { Bell } from "lucide-react";
import { 
  getNotificationList, 
  readNotification, 
  readAllNotifications, 
  getUnreadCount 
} from "src/api";

// [SBCHOI] Component Add
import SignupModal from './SignUpModal';
import axios from "axios";
import GoodsModal from "@/domain/goods/components/GoodsModal";
import Modal from "react-modal";
import ImageCrop from "src/domain/author/ImageCrop";
import LoginModal from "../modal/LoginModal";
import { clearStoredAuth, saveUserSession } from '../../auth/userSessionStorage';



interface HeaderProps {
    searchValue: string;
    onChange: (e: any) => void;
    name: string;
    activeFlag: string;
}

const Header = ({ searchValue, onChange, name, activeFlag }: HeaderProps) => {
    const navigate = useNavigate();
    const [view, setView] = useState(false);
    const [token, setToken] = useState('');

    // 알림 관련 상태
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState<any[]>([]);
    const [isNotiOpen, setIsNotiOpen] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const menuIconRef = useRef<HTMLDivElement>(null);

    // 알림 관련 Refs
    const notiDropdownRef = useRef<HTMLDivElement>(null);
    const notiIconRef = useRef<HTMLDivElement>(null);

    // 안 읽은 알림 카운트 갱신 함수
    const fetchUnreadCount = useCallback(() => {
        const token = localStorage.getItem('jwtToken');
        if (!token) return;
        getUnreadCount()
            .then(data => {
                if (data && typeof data.unreadCount === 'number') {
                    setUnreadCount(data.unreadCount);
                }
            })
            .catch(err => console.error("Failed to fetch unread count:", err));
    }, []);

    // 알림 리스트 갱신 함수
    const fetchNotifications = useCallback(() => {
        const token = localStorage.getItem('jwtToken');
        if (!token) return;
        getNotificationList()
            .then(data => {
                if (data && data.notifications && Array.isArray(data.notifications.content)) {
                    setNotifications(data.notifications.content);
                } else if (data && Array.isArray(data.content)) {
                    setNotifications(data.content);
                } else if (data && data.notifications && Array.isArray(data.notifications)) {
                    setNotifications(data.notifications);
                }
            })
            .catch(err => console.error("Failed to fetch notifications:", err));
    }, []);

    // 알림 아이콘 클릭 핸들러
    const handleNotiClick = () => {
        if (!isNotiOpen) {
            fetchNotifications();
            fetchUnreadCount();
        }
        setIsNotiOpen(prev => !prev);
    };

    // 알림 개별 읽음 처리 핸들러
    const handleReadNoti = (id: number, url: string) => {
        // 즉시 UI에 읽음 상태 반영 (아이콘/배경 변경)
        setNotifications(prev =>
            prev.map(noti => (noti.id === id ? { ...noti, isRead: true, read: true } : noti))
        );
        setUnreadCount(prev => Math.max(0, prev - 1));

        readNotification(id).catch(err => console.error("Failed to read notification:", err));

        // 알림에 연결된 상세페이지로 이동
        if (url) {
            navigate(url);
        }
    };

    // 알림 전체 읽음 처리 핸들러
    const handleReadAllNotis = () => {
        readAllNotifications()
            .then(() => {
                fetchUnreadCount();
                fetchNotifications();
            })
            .catch(err => console.error("Failed to read all notifications:", err));
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
                menuIconRef.current && !menuIconRef.current.contains(event.target as Node)) {
                setView(false);
            }
            if (notiDropdownRef.current && !notiDropdownRef.current.contains(event.target as Node) &&
                notiIconRef.current && !notiIconRef.current.contains(event.target as Node)) {
                setIsNotiOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };

    }, []);

    // 1분 주기 미확인 알림 수 폴링 및 로그인 성공 시점 갱신
    useEffect(() => {
        const jwtToken = localStorage.getItem('jwtToken');
        if (jwtToken) {
            fetchUnreadCount();
            const interval = setInterval(fetchUnreadCount, 60000);
            return () => clearInterval(interval);
        }
    }, [token, fetchUnreadCount]);


    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkIfMobile = () => {
            if (window.innerWidth <= 1500) {
                setIsMobile(true);
            } else {
                setIsMobile(false);
            }
        };

        checkIfMobile();

        window.addEventListener('resize', checkIfMobile);

        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);



    const [tab, setTab] = useState([
        {
            isActive: activeFlag === "author",
            index: 0,
            router: "/author",
            name: "작가의 서재",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none">
                    <mask id="a" width="24" height="24" x="0" y="0" maskUnits="userSpaceOnUse">
                        <path fill="#D9D9D9" d="M0 0h24v24H0z" />
                    </mask><g mask="url(#a)">
                        <path fill="#1C1B1F" d="M6 19h3v-6h6v6h3v-9l-6-4.5L6 10v9Zm-2 2V9l8-6 8 6v12h-7v-6h-2v6H4Z" />
                    </g></svg>
            ),
        },
        {
            isActive: activeFlag === "book",
            index: 1,
            router: "/book",
            name: "작품 스토리",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <mask id="a"
                        width="24"
                        height="24"
                        x="0"
                        y="0" maskUnits="userSpaceOnUse">
                        <path fill="#D9D9D9" d="M0 0h24v24H0z" />
                    </mask>
                    <g mask="url(#a)">
                        <path fill="#1C1B1F"
                            d="M6.5 16c.783 0 1.546.087 2.287.262.742.175 1.48.438 2.213.788V7.2c-.683-.4-1.408-.7-2.175-.9a9.175 9.175 0 0 0-4.112-.125A8.665 8.665 0 0 0 3 6.7v9.9c.583-.2 1.162-.35 1.737-.45C5.313 16.05 5.9 16 6.5 16Zm6.5 1.05c.733-.35 1.47-.613 2.213-.788a10.268 10.268 0 0 1 4.05-.113c.575.101 1.154.251 1.737.451V6.7a8.664 8.664 0 0 0-1.712-.525 9.218 9.218 0 0 0-4.113.125c-.767.2-1.492.5-2.175.9v9.85ZM12 20a9.6 9.6 0 0 0-2.6-1.475A8.193 8.193 0 0 0 6.5 18c-.7 0-1.388.092-2.063.275a9.05 9.05 0 0 0-1.937.775c-.35.183-.688.175-1.012-.025A.966.966 0 0 1 1 18.15V6.1c0-.183.046-.358.137-.525A.863.863 0 0 1 1.55 5.2c.767-.4 1.567-.7 2.4-.9.833-.2 1.683-.3 2.55-.3A10.78 10.78 0 0 1 12 5.5 10.78 10.78 0 0 1 17.5 4c.867 0 1.717.1 2.55.3.833.2 1.633.5 2.4.9.183.083.32.208.413.375.091.167.137.342.137.525v12.05a.966.966 0 0 1-.488.875c-.325.2-.662.208-1.012.025a9.05 9.05 0 0 0-1.938-.775A7.836 7.836 0 0 0 17.5 18c-1 0-1.967.175-2.9.525A9.6 9.6 0 0 0 12 20Z" />
                    </g>
                </svg>
            ),
        },
        {
            isActive: activeFlag === "postcard",
            index: 2,
            router: "/postcard",
            name: "엽서북",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none">
                    <mask id="a"
                        width="24"
                        height="24"
                        x="0"
                        y="0"
                        maskUnits="userSpaceOnUse" >
                        <path fill="#D9D9D9" d="M0 0h24v24H0z" />
                    </mask>
                    <g mask="url(#a)">
                        <path fill="#1C1B1F"
                            d="M5 22c-.55 0-1.02-.196-1.413-.587A1.926 1.926 0 0 1 3 20V6c0-.55.196-1.02.587-1.412A1.926 1.926 0 0 1 5 4h3l4-4 4 4h3c.55 0 1.02.196 1.413.588.391.391.587.862.587 1.412v14c0 .55-.196 1.02-.587 1.413A1.926 1.926 0 0 1 19 22H5Zm0-2h14V6H5v14Zm1-2h12l-3.75-5-3 4L9 14l-3 4Zm4.1-14h3.8L12 2.1 10.1 4Z" />
                    </g>
                </svg>
            ),
        },
        {
            isActive: activeFlag === "bookpair",
            index: 3,
            router: "/bookpair",
            name: "북페어 캘린더",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <mask id="a" width="24" height="24" x="0" y="0" maskUnits="userSpaceOnUse">
                        <path fill="#D9D9D9" d="M0 0h24v24H0z" />
                    </mask>
                    <g mask="url(#a)">
                        <path fill="#1C1B1F" d="M5 22c-.55 0-1.021-.196-1.413-.587A1.926 1.926 0 0 1 3 20V6c0-.55.196-1.021.587-1.413A1.926 1.926 0 0 1 5 4h1V2h2v2h8V2h2v2h1c.55 0 1.021.196 1.413.587.391.392.587.863.587 1.413v14c0 .55-.196 1.021-.587 1.413A1.926 1.926 0 0 1 19 22H5Zm0-2h14V10H5v10Z"/>
                    </g>
                </svg>
            ),
        },
    ]);

    const onTabClick = (e: any) => {
        const index = e.currentTarget.dataset.index;
        const router = tab[index].router;
        const tempTab = tab.map((v: any) => {
            let temp = {
                ...v,
                isActive: v.index === parseInt(index),
            };
            if (v.index == index) {
                temp = {
                    ...v,
                    isActive: true,
                };
            }
            return temp;
        });
        setTab(tempTab);
        navigate(router);
    };

    const [showSignup, setShowSignup] = useState(false);
    const [nickName, setNickName] = useState(() => localStorage.getItem('nickName') || '로그인');
    const [authorStatus, setAuthorStatus] = useState(() => localStorage.getItem('authorStatus') || '');
    const [showSidebar, setShowSidebar] = useState(false);



    // SBCHOI ADD
    // JWT

    const handleLogoutState = useCallback(() => {
        clearStoredAuth();
        setToken('');
        setNickName('로그인');
        setAuthorStatus('');
        localStorage.removeItem('sessionKey');
    }, []);

    useEffect(() => {
        const jwtToken = localStorage.getItem('jwtToken');
        if (!jwtToken) return;

        // /api/sign/oauth2/who 하나로 토큰 유효성 검사와 사용자 정보 로드를 통합 수행
        axios.post('/api/sign/oauth2/who', null, {
            headers: {
                Authorization: `Bearer ${jwtToken}`,
            },
        })
            .then(async response => {
                const { email, token: newToken, nickName: newNick, authorStatus: newStatus } = response.data;
                
                let resolvedNick = newNick;
                const effectiveToken = newToken || jwtToken;
                if (newStatus && newStatus.toLowerCase() === "hold") {
                    try {
                        const profileResponse = await axios.post('/api/user/info', null, {
                            headers: {
                                Authorization: `Bearer ${effectiveToken}`,
                            },
                        });
                        if (profileResponse.data && profileResponse.data.authorInfo) {
                            resolvedNick = profileResponse.data.authorInfo.nickname + " 작가님";
                        }
                    } catch (e) {
                        console.error("Failed to fetch author name for HOLD writer:", e);
                    }
                }
                
                if (!resolvedNick) {
                    resolvedNick = newNick || '사용자';
                }

                saveUserSession({
                    jwtToken: effectiveToken,
                    email,
                    authorStatus: newStatus,
                    nickName: resolvedNick,
                });

                setToken(effectiveToken);
                setNickName(resolvedNick);
                setAuthorStatus(newStatus);
            })
            .catch((error) => {
                console.error("Auth verification failed:", error);
                // 401 에러 등 토큰이 유효하지 않은 경우에만 상태 초기화
                if (error.response && (error.response.status === 401 || error.response.status === 400)) {
                    handleLogoutState();
                }
            });
    }, [handleLogoutState]);

    useEffect(() => {
        const handleLoginSuccess = () => {
            setNickName(localStorage.getItem('nickName') || '로그인');
            setAuthorStatus(localStorage.getItem('authorStatus') || '');
            const jwtToken = localStorage.getItem('jwtToken');
            if (jwtToken) {
                setToken(jwtToken);
            }
            fetchUnreadCount();
        };
        window.addEventListener('login_success', handleLoginSuccess);
        return () => window.removeEventListener('login_success', handleLoginSuccess);
    }, [fetchUnreadCount]);

    const closeModal = () => {
        setShowSignup(false);
    };



    const handleClick = () => {
        if (nickName !== "로그인") {
            // axios header 에 있는 jwt 토큰 기반 으로 해서 로그아웃 처리
            axios.post('/api/sign/oauth2/logout')
                .then((res) => {
                    handleLogoutState();
                    if (isMobile) setView(false);
                    window.location.replace('/');
                })
                .catch((error) => {
                    console.error("Logout failed:", error);
                    // 로그아웃 API가 실패하더라도 클라이언트 상태는 초기화하여 강제 로그아웃 처리
                    handleLogoutState();
                    window.location.replace('/');
                });

            } else {

                setShowSignup(true);
            }

    };

    const toggleSidebar = () => {

        setShowSidebar(prev => !prev);
    };

    return (
        <>
        {showSignup && <LoginModal setNickName={setNickName} token={token} setToken={setToken}
                                   onClose={() => setShowSignup(false)} closeModal={closeModal}/>}
        <header css={headerWrapCss}>
            <h3 css={logoBoxCss}>
                <a href="/" id="home">
                    <img src="/logo2.svg" alt="logo"/>
                </a>
            </h3>


            {isMobile ? (
                <div className="flex items-center gap-4 ml-auto">
                    {nickName !== '로그인' && (
                        <div className="relative flex items-center" ref={notiIconRef}>
                            <button 
                                onClick={handleNotiClick}
                                className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                                    isNotiOpen 
                                        ? "bg-[#E8FBF2] text-[#19CF7A]" 
                                        : "bg-transparent text-[#282A37] hover:bg-[#F3F4F6]"
                                }`}
                            >
                                <Bell size={22} className={isNotiOpen ? "fill-current" : "stroke-[1.8]"} />
                                {unreadCount > 0 && (
                                    <span className="absolute top-0 right-0 bg-[#FF4D4F] text-white text-[10px] font-bold rounded-full w-[18px] h-[18px] flex items-center justify-center border border-white">
                                        {unreadCount}
                                    </span>
                                )}
                            </button>
                            {isNotiOpen && (
                                <NotificationDropdown 
                                    notifications={notifications}
                                    unreadCount={unreadCount}
                                    onRead={handleReadNoti}
                                    onReadAll={handleReadAllNotis}
                                    onClose={() => setIsNotiOpen(false)}
                                    dropdownRef={notiDropdownRef}
                                />
                            )}
                        </div>
                    )}
                    <div ref={menuIconRef} className="relative">
                        <MobileMenu onClick={() => {
                            setView(!view)
                        }}/>
                        {view && <DropdownMenu handleClick={handleClick}  closeSidebar={() => setView(false)} isUser={authorStatus} token={token} setToken={setToken} nickName={nickName} dropdownRef={dropdownRef}   onLoginClick={() => {
                            setShowSignup(true);
                        }} />}
                    </div>
                </div>
            ) : (

                <>


                <div className={"flex justify-center w-full"}>

                    <div css={menuCss}>
                        {tab.map((v) => (
                            <li
                                key={v.index}
                                data-index={v.index}
                                onClick={onTabClick}
                            >
                                <div className={v.isActive ? "textActive" : "textDeactive"}>
                                    {v.name}
                                </div>
                            </li>
                        ))}
                    </div>




                </div>




                    <div css={authButtonsCss}>


                        {nickName != '로그인' && <>

                            <div className="flex items-center gap-[20px] ">

                                {/* 알림 아이콘 (로그아웃 버튼 왼쪽에 배치) */}
                                <div className="relative flex items-center" ref={notiIconRef}>
                                    <button 
                                        onClick={handleNotiClick}
                                        className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                                            isNotiOpen 
                                                ? "bg-[#E8FBF2] text-[#19CF7A]" 
                                                : "bg-transparent text-[#282A37] hover:bg-[#F3F4F6]"
                                        }`}
                                    >
                                        <Bell size={22} className={isNotiOpen ? "fill-current" : "stroke-[1.8]"} />
                                        {unreadCount > 0 && (
                                            <span className="absolute top-0 right-0 bg-[#FF4D4F] text-white text-[10px] font-bold rounded-full w-[18px] h-[18px] flex items-center justify-center border border-white">
                                                {unreadCount}
                                            </span>
                                        )}
                                    </button>
                                    {isNotiOpen && (
                                        <NotificationDropdown 
                                            notifications={notifications}
                                            unreadCount={unreadCount}
                                            onRead={handleReadNoti}
                                            onReadAll={handleReadAllNotis}
                                            onClose={() => setIsNotiOpen(false)}
                                            dropdownRef={notiDropdownRef}
                                        />
                                    )}
                                </div>

                                <button onClick={() => handleClick()}
                                        style={{borderRadius: '100px'}}
                                        className="flex justify-center  w-32 h-11 items-center border-[1px] text-[#282A37] text-base font-semibold bg-white  border-[#282a37] gap-[10px] p-3 px-10 ">
                                    로그아웃
                                </button>

                                {nickName.split(' ').length > 1 && nickName.split(' ')[1] === "작가님" ? (
                                    <>
                                        <Link to={`/mypage`}>
                                            <button
                                                id="loginButton"
                                                style={{borderRadius: '100px'}}
                                                className="items-center border-[1px] text-white text-base font-semibold  gap-[10px] p-3  shadow-sm justify-center w-32 h-11  px-10 py-3 flex bg-main">
                                                나의 책장
                                            </button>
                                        </Link>

                                        <div>
                                <span className="text-lg text-right text-black font-semibold">{nickName} <span
                                    className="text-sm font-normal text-black">, 안녕하세요!</span></span>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <Link to={`/mypage`}>
                                            <button
                                                id="loginButton"
                                                style={{borderRadius: '100px'}}
                                                className="items-center border-[1px] text-white text-base font-semibold  gap-[10px] p-3  shadow-sm justify-center w-32 h-11  px-10 py-3 flex bg-main">
                                                나의 책갈피
                                            </button>
                                        </Link>

                                        <div>
                                <span className="text-lg text-right text-black font-semibold">{nickName} <span
                                    className="text-sm font-normal text-black">님, 안녕하세요!</span></span>
                                        </div>
                                    </>
                                )}

                            </div>


                            {/*  <div id="userProfile" className="justify-center w-24 h-9 flex ml-4 mb-2">
                        <img src={profile} className="w-10 h-10 border-2 border-main rounded-full ml-4 mb-2 mr-2"
                             alt='profile'
                             onClick={()=> setView(true)}
                             />

                    </div>*/}

                            {/* <div css={smallIconCss}>
                    <Menu onClick={() => {
                        setView(!view)
                    }}/>

                    {view && <DropdownMenu handleClick={handleClick} isUser={false} nickName={nickName}/>}
                </div>*/}

                        </>
                        }

                        {nickName == '로그인' && <>
                            <div id="loginButton"
                                 className="shadow-sm justify-center w-32 h-11  px-10 py-2.5 flex bg-main"
                                 style={{borderRadius: '100px'}}>
                                <p className="text-white font-medium text-base text-center" onClick={() => {
                                    if (nickName === '로그인') {
                                        setShowSignup(true);
                                    } else {
                                        setView(!view);
                                    }
                                }}>
                                    {nickName}
                                </p>
                            </div>
                        </>

                        }




                        {/*  <Modal
                isOpen={showSignup}
                onRequestClose={closeModal}
                contentLabel="login Modal"
                className="modal-login"
                ariaHideApp={false}
                overlayClassName="overlay">
                <SignupModal setNickName={setNickName} token={token} setToken={setToken}
                             onClose={() => setShowSignup(false)} closeModal={closeModal}/>
            </Modal>
*/}

                        {/* <div css={smallIconCss}>
                <Menu onClick={() => {
                    setView(!view)
                }}/>

                {view && <DropdownMenu handleClick={handleClick} isUser={false} nickName={nickName}/>}
            </div>*/}

                    </div>
                </>

            )
            }


        </header>
        </>
    )
        ;
};

export default Header;
